import { Close, Tune } from "@mui/icons-material";
import { Badge, Box, Button, Stack } from "@mui/material";
import { HeaderIconButton } from "./layout/PageHeader";
import { TransitionModal } from "./modal";
import { useEffect, useMemo, useState } from "react";
import { useGetValueOptions } from "core/value_options/hooks";
import { toast } from "react-toastify";
import FieldAutocompleteList from "./forms/fields/FieldAutocompleteList";
import { FormProvider, useForm } from "react-hook-form";

type SearchFiltersProps = {
  onFiltersUpdate: (data: any) => void;
  currentFiltersCount?: number;
};

const TagList = ({ tags, onRemove }: { tags?: [string, string][], onRemove: (tag: string) => void }) => {
  if (!tags) return null;

  return (
    <Stack direction="row" gap={1} flexWrap="wrap">
      {tags.map((tag) => (
        <Button key={tag[0]} color="info" variant="outlined" sx={{ fontSize: 14 }} onClick={() => onRemove(tag[0])} endIcon={<Close />}>{tag[1]}</Button>
      ))}
    </Stack>
  )
}

const sameArray = (a: string[] = [], b: string[] = []) => {
  return a.length === b.length && a.every((v, i) => b.includes(v));
}

const SearchFilters: React.FC<SearchFiltersProps> = ({ onFiltersUpdate, currentFiltersCount = 0 }) => {
  const [open, setOpen] = useState<boolean>(false);
  const [baseFilters, setBaseFilters] = useState<{ qualification?: string[] }>({});
  const [filters, setFilters] = useState<{ qualification?: string[] }>({});
  const {
    data: valueOptions,
  } = useGetValueOptions({
    namespace: "anael",
    category: "qualification",
    onError: (err) => toast.error("une erreur est survenue"),
  });

  const qualificationOptions = useMemo(
    () =>
      valueOptions?.options?.reduce((a: any, v: any) => {
        a.push([v.value, v.label]);
        return a;
      }, []) || [],
    [valueOptions]
  );

  const methods = useForm();
  const { control, handleSubmit, setValue } = methods;

  const onSubmit = () => {
    if (!sameArray(baseFilters.qualification, filters.qualification)) {
      onFiltersUpdate(filters);
    }

    setOpen(false);
  };

  const resetFilers = () => {
    setValue('qualification', null);
    setFilters({});
  }

  const addQualificationFilter = (value: string) => {
    setValue('qualification', null);
    setFilters({ qualification: [...(filters.qualification || []), value] });
  }

  const removeQualificationFilter = (value: string) => {
    if (filters.qualification?.length === 1) {
      setFilters({});
    } else {
      setFilters({ qualification: filters.qualification?.filter((t) => t !== value) });
    }
  }

  const cancel = () => {
    setFilters(baseFilters);
    setOpen(false);
  };

  useEffect(() => {
    if (open) {
      setBaseFilters(filters);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, setBaseFilters])

  const canResetFilters = filters.qualification && filters.qualification.length > 0;

  return (
    <>
      <Badge badgeContent={currentFiltersCount} color="secondary" overlap="circular" sx={{ '& .MuiBadge-badge': { fontSize: '0.625rem', minWidth: 16, height: 16, top: 6, right: 6 } }}>
        <HeaderIconButton onClick={() => setOpen(true)} aria-label="Filtres de recherche" sx={{ color: currentFiltersCount ? 'secondary.main' : undefined }}>
          <Tune sx={{ fontSize: '1.25rem' }} />
        </HeaderIconButton>
      </Badge>
      <TransitionModal
        open={open}
        onClose={() => setOpen(false)} title="Filtres de recherche"
        fullHeight={false}
        fullWidth={false}
        titleBorder
      >
        <Box sx={{ mt: 6, p: 4, width: 600 }}>
          <FormProvider {...methods}>
            <Stack justifyContent="space-between">
              <Stack>
                <FieldAutocompleteList
                  control={control}
                  options={qualificationOptions.filter((option: [string, string]) => !filters.qualification || !filters.qualification.includes(option[0]))}
                  fieldName="qualification"
                  labelId="qualification"
                  label="Rechercher par qualification principale"
                  onSelect={addQualificationFilter}
                />
                <TagList
                  tags={qualificationOptions.filter((option: [string, string]) => filters.qualification?.includes(option[0]))}
                  onRemove={removeQualificationFilter}
                />
              </Stack>

              <Stack direction="row" justifyContent="space-between" mt={6}>
                <Box>
                  {canResetFilters && <Button variant="outlined" color="primary" onClick={resetFilers}>Réinitialiser les filtres</Button>}
                </Box>
                <Stack direction="row" gap={2}>
                  <Button variant="contained" color="primary" onClick={handleSubmit(onSubmit)}>Appliquer</Button>
                  <Button variant="outlined" color="error" onClick={cancel}>Annuler</Button>
                </Stack>
              </Stack>
            </Stack>
          </FormProvider>
        </Box>
      </TransitionModal>
    </>
  )
};

export default SearchFilters;
